/**
 * The subtitle look and layout (1.9.0), shared by every renderer: the asset player's overlay, the editor's program
 * viewer and Side by side, the share page and the burn-in renderer. Dependency-free (no zod, no other module), so the
 * share page and the graphics renderer import it as `@prism/contracts/caption`.
 *
 *  - `CAPTION`: the geometry and the look (font, weight, line height, padding, radius, speaker colour). Nothing about
 *    the look lives in CSS any more, so the preview and the burn-in cannot drift apart.
 *  - `effectiveCueStyle(track, cue)`: the default, then the track's style, then the cue's; a leading `{\anN}` in an
 *    old cue's text (SRT files imported before 1.9.0) gives its cell when the cue's style sets no `align`.
 *  - `cueLayout(w, h, style, subtitleBottom?)`: where and how one cue is drawn in a w × h frame, as container anchors
 *    (flex alignment and paddings of a full-frame container, text-align, font px, colours), not box pixels: a middle
 *    row cue's box depends on the text's measured height. `cueContainerCss` and `cueBoxCss` turn it into CSS.
 *  - `cueRuns(text)`: the one tokenizer for cue text. It keeps `<i>`, `<b>` and `<u>` only, drops every other tag and
 *    `{\…}` code, and decodes the basic entities; renderers build text nodes from the runs, never HTML.
 *  - `cueIntervals(cues)`: the cues split into intervals, each with the set of cues shown in it (the burn-in draws one
 *    full-frame picture per interval, so cues shown at once are all drawn and compose keeps one stream).
 */

/** The 9 placement cells: row t(op), m(iddle), b(ottom) × column l(eft), c(entre), r(ight). */
export const CUE_ALIGNS = ['tl', 'tc', 'tr', 'ml', 'mc', 'mr', 'bl', 'bc', 'br'] as const;
export type CueAlign = (typeof CUE_ALIGNS)[number];
export type CueRow = 't' | 'm' | 'b';
export type CueColumn = 'l' | 'c' | 'r';

/** A cue's or a track's style: every field optional (contracts subtitles.ts CueStyleSchema / TrackStyleSchema). */
export interface CueStyle {
  align?: CueAlign;
  /** Lowercase #rrggbb. */
  color?: string;
  /** The opacity of the black box behind the text, 0–1. */
  background?: number;
  /** A scale of the default size, 0.5–2. */
  size?: number;
}

/** A style with every field set: what a renderer draws. */
export interface ResolvedCueStyle {
  align: CueAlign;
  color: string;
  background: number;
  size: number;
}

export const DEFAULT_TRACK_STYLE: Readonly<ResolvedCueStyle> = Object.freeze({ align: 'bc', color: '#ffffff', background: 0.62, size: 1 });

/** The style limits (the zod schemas and the editors use the same). */
export const CUE_STYLE_LIMITS = { backgroundMin: 0, backgroundMax: 1, sizeMin: 0.5, sizeMax: 2 } as const;

/**
 * The subtitle geometry and look. Geometry (1.7.0): the font from the short side (min(w, h) × 0.045), the bottom
 * margin (7.5 % of the height for landscape and square, 20 % for portrait, above the platforms' buttons; or the
 * format's subtitle height), at most 80 % of the width. 1.9.0: the top margin by orientation (portrait below the
 * platforms' top bar), the 10 % side margins for left and right cells, and the look moved here from styles.css and
 * graphics-host.ts.
 */
export const CAPTION = {
  fontOfShortSide: 0.045,
  bottomLandscape: 0.075,
  bottomPortrait: 0.2,
  topLandscape: 0.075,
  topPortrait: 0.14,
  /** Left and right cells sit against this margin (a fraction of the width). */
  side: 0.1,
  maxWidth: 0.8,
  fontFamily: '"Inter","Noto Sans","Noto Sans Hebrew","Noto Sans Arabic",sans-serif',
  fontWeight: 600,
  lineHeight: 1.28,
  /** The box's padding in em: vertical, horizontal. */
  paddingEm: [0.12, 0.45] as const,
  radiusEm: 0.18,
  speakerColor: '#c9c2ff',
} as const;

export const cueRow = (align: CueAlign): CueRow => align[0] as CueRow;
export const cueColumn = (align: CueAlign): CueColumn => align[1] as CueColumn;

/** SRT `{\anN}` (numpad layout: 7 8 9 top, 4 5 6 middle, 1 2 3 bottom) to a cell, and back. */
export const ALIGN_FROM_NUMPAD: Readonly<Record<number, CueAlign>> = Object.freeze({ 7: 'tl', 8: 'tc', 9: 'tr', 4: 'ml', 5: 'mc', 6: 'mr', 1: 'bl', 2: 'bc', 3: 'br' });
export const NUMPAD_FROM_ALIGN: Readonly<Record<CueAlign, number>> = Object.freeze({ tl: 7, tc: 8, tr: 9, ml: 4, mc: 5, mr: 6, bl: 1, bc: 2, br: 3 });

const LEGACY_AN = /^\s*\{\\an([1-9])\}/;

/** The cell a leading `{\anN}` in the text gives, or undefined. */
export function legacyAlign(text: string): CueAlign | undefined {
  const m = LEGACY_AN.exec(text);
  return m ? ALIGN_FROM_NUMPAD[Number(m[1])] : undefined;
}

/** The text without a leading `{\anN}`. */
export const stripLegacyAlign = (text: string): string => text.replace(LEGACY_AN, '');

/**
 * The style a cue is drawn with: the default, then the track's fields, then the cue's. The cell is the cue's own
 * `align`, else a leading `{\anN}` in its text (tracks imported before 1.9.0 keep their placement), else the track's.
 */
export function effectiveCueStyle(track: CueStyle | null | undefined, cue: { text: string; style?: CueStyle | null }): ResolvedCueStyle {
  const s = cue.style ?? {};
  const t = track ?? {};
  return {
    align: s.align ?? legacyAlign(cue.text) ?? t.align ?? DEFAULT_TRACK_STYLE.align,
    color: s.color ?? t.color ?? DEFAULT_TRACK_STYLE.color,
    background: s.background ?? t.background ?? DEFAULT_TRACK_STYLE.background,
    size: s.size ?? t.size ?? DEFAULT_TRACK_STYLE.size,
  };
}

/** Whether a resolved style is the default one (a 1.8.3 renderer draws only that). */
export const isDefaultCueStyle = (s: ResolvedCueStyle): boolean =>
  s.align === DEFAULT_TRACK_STYLE.align && s.color === DEFAULT_TRACK_STYLE.color && s.background === DEFAULT_TRACK_STYLE.background && s.size === DEFAULT_TRACK_STYLE.size;

/* ---------------------------------------------------------------- layout */

/**
 * One cue's layout in a frame of `width` × `height` (CSS or output pixels), as anchors of a full-frame container:
 * the container is `position:absolute; inset:0; display:flex; flex-direction:column`, `justify` places the box
 * vertically and `align` horizontally, inside the paddings. The box is at most `maxWidthPx` wide.
 */
export interface CueLayout {
  row: CueRow;
  column: CueColumn;
  /** flex-direction column: justify-content (vertical). */
  justify: 'flex-start' | 'center' | 'flex-end';
  /** flex-direction column: align-items (horizontal). */
  align: 'flex-start' | 'center' | 'flex-end';
  paddingTopPx: number;
  paddingBottomPx: number;
  paddingLeftPx: number;
  paddingRightPx: number;
  textAlign: 'left' | 'center' | 'right';
  fontPx: number;
  maxWidthPx: number;
  color: string;
  /** rgba(0,0,0,a), or 'transparent' at 0. */
  background: string;
  /** The bottom margin used for a bottom-row cue (a fraction of the height): subtitleBottom or the default. */
  bottom: number;
}

const FLEX = { t: 'flex-start', m: 'center', b: 'flex-end', l: 'flex-start', c: 'center', r: 'flex-end' } as const;
const round3 = (n: number) => Math.round(n * 1000) / 1000;

export function cueLayout(width: number, height: number, style: ResolvedCueStyle, subtitleBottom?: number): CueLayout {
  const row = cueRow(style.align), column = cueColumn(style.align);
  const portrait = height > width;
  const bottom = subtitleBottom ?? (portrait ? CAPTION.bottomPortrait : CAPTION.bottomLandscape);
  const top = portrait ? CAPTION.topPortrait : CAPTION.topLandscape;
  const side = Math.round(width * CAPTION.side);
  const bg = Math.min(1, Math.max(0, style.background));
  return {
    row,
    column,
    justify: FLEX[row],
    align: FLEX[column],
    paddingTopPx: row === 't' ? Math.round(height * top) : 0,
    paddingBottomPx: row === 'b' ? Math.round(height * bottom) : 0,
    paddingLeftPx: column === 'l' ? side : 0,
    paddingRightPx: column === 'r' ? side : 0,
    textAlign: column === 'l' ? 'left' : column === 'r' ? 'right' : 'center',
    fontPx: Math.round(Math.min(width, height) * CAPTION.fontOfShortSide * style.size),
    maxWidthPx: Math.round(width * CAPTION.maxWidth),
    color: style.color,
    background: bg > 0 ? `rgba(0,0,0,${round3(bg)})` : 'transparent',
    bottom,
  };
}

/** The container's CSS (camelCase properties, for a React style object or `Object.assign(el.style, …)`). */
export function cueContainerCss(l: CueLayout): Record<string, string> {
  return {
    position: 'absolute', left: '0', top: '0', right: '0', bottom: '0', display: 'flex', flexDirection: 'column',
    justifyContent: l.justify, alignItems: l.align, boxSizing: 'border-box', pointerEvents: 'none',
    paddingTop: `${l.paddingTopPx}px`, paddingBottom: `${l.paddingBottomPx}px`, paddingLeft: `${l.paddingLeftPx}px`, paddingRight: `${l.paddingRightPx}px`,
  };
}

/** The text box's CSS. */
export function cueBoxCss(l: CueLayout): Record<string, string> {
  return {
    maxWidth: `${l.maxWidthPx}px`, textAlign: l.textAlign, fontFamily: CAPTION.fontFamily, fontWeight: String(CAPTION.fontWeight),
    fontSize: `${l.fontPx}px`, lineHeight: String(CAPTION.lineHeight), color: l.color, background: l.background,
    padding: `${CAPTION.paddingEm[0]}em ${CAPTION.paddingEm[1]}em`, borderRadius: `${CAPTION.radiusEm}em`,
    whiteSpace: 'pre-wrap', unicodeBidi: 'plaintext', overflowWrap: 'break-word',
  };
}

/* ---------------------------------------------------------------- text runs */

export interface CueRun {
  text: string;
  i?: true;
  b?: true;
  u?: true;
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', lrm: '‎', rlm: '‏' };
const decodeEntities = (s: string) => s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
  if (e[0] === '#') {
    const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
  }
  return ENTITIES[e.toLowerCase()] ?? m;
});

/**
 * Cue text as runs of plain text with italic, bold and underline flags. `<i>`, `<b>`, `<u>` (any case, VTT classes
 * such as `<i.loud>` allowed) open and close them; every other tag (`<font>`, `<v Name>`, `<c>`, `<img …>`) and every
 * `{\…}` code is dropped; a `<` that does not start a tag stays text. Entities are decoded. Adjacent runs with the
 * same flags are joined; newlines stay in the text (renderers use pre-wrap).
 */
export function cueRuns(text: string): CueRun[] {
  const runs: CueRun[] = [];
  const depth = { i: 0, b: 0, u: 0 };
  const push = (raw: string) => {
    if (!raw) return;
    const t = decodeEntities(raw);
    if (!t) return;
    const run: CueRun = { text: t };
    if (depth.i) run.i = true;
    if (depth.b) run.b = true;
    if (depth.u) run.u = true;
    const last = runs[runs.length - 1];
    if (last && last.i === run.i && last.b === run.b && last.u === run.u) last.text += t;
    else runs.push(run);
  };
  const re = /<(\/?)([a-z][a-z0-9]*)(?:[.\s][^>]*)?>|<\/?[a-z][^>]*>|\{\\[^}]*\}/gi;
  let at = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    push(text.slice(at, m.index));
    at = m.index + m[0].length;
    const name = m[2]?.toLowerCase();
    if (name === 'i' || name === 'b' || name === 'u') {
      if (m[1]) depth[name] = Math.max(0, depth[name] - 1);
      else depth[name] += 1;
    }
  }
  push(text.slice(at));
  return runs;
}

/** The text of the runs (markup gone): what reading-speed counts and plain-text readers see. */
export const runsText = (runs: readonly CueRun[]): string => runs.map((r) => r.text).join('');

/* ---------------------------------------------------------------- intervals */

export interface CueInterval<T> {
  startTicks: number;
  endTicks: number;
  /** The cues shown in the interval, by start and then input order. */
  cues: T[];
  /** The ids of those cues joined with '+': the same set gives the same key (one picture for it). */
  key: string;
}

/**
 * The cues split at every start and end into intervals with at least one cue shown, each with the set shown in it.
 * A track whose cues never overlap gives one interval per cue, its own times and its id as the key (as before 1.9.0).
 * Cues that end at or before they start are left out.
 */
export function cueIntervals<T extends { id: string; startTicks: number; endTicks: number }>(cues: readonly T[]): Array<CueInterval<T>> {
  const valid = cues.map((c, i) => ({ c, i })).filter(({ c }) => c.endTicks > c.startTicks);
  valid.sort((a, b) => a.c.startTicks - b.c.startTicks || a.i - b.i);
  const edges = [...new Set(valid.flatMap(({ c }) => [c.startTicks, c.endTicks]))].sort((a, b) => a - b);
  const out: Array<CueInterval<T>> = [];
  // A sweep: `active` holds the cues with start <= a < end, in start order; no edge lies inside (a, b), so they are
  // exactly the cues shown in [a, b).
  let p = 0;
  let active: typeof valid = [];
  for (let k = 0; k + 1 < edges.length; k++) {
    const a = edges[k]!, b = edges[k + 1]!;
    while (p < valid.length && valid[p]!.c.startTicks <= a) active.push(valid[p++]!);
    active = active.filter(({ c }) => c.endTicks > a);
    if (active.length) {
      const shown = active.map(({ c }) => c);
      out.push({ startTicks: a, endTicks: b, cues: shown, key: shown.map((c) => c.id).join('+') });
    }
  }
  return out;
}
